import React, { useCallback, useEffect, useState } from 'react';
import styles from './ProjectEdition.module.css';
import { CustomButton, CustomFileInput, CustomIcon, CustomInputText, CustomLoader, CustomNotification, CustomSwitch, CustomTextArea, CustomTitle } from '../../../components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { createProject, editProject, getProjectById } from '../../../services/projectService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { AxiosResponse } from 'axios';
import formatDate from '../../../utils/date';
import { unzipSync } from 'fflate';

const ProjectEdition = () => {

    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { projectId, isEditing } = location.state || {};
    const userId : number = useSelector((state: RootState) => state.auth.userId);
    const isTrustedUser : boolean = useSelector((state: RootState) => state.auth.isTrustedUser);

    const [isLoading, setIsLoading] = useState(true);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [notificationMsg, setNotificationMsg] = useState('');
    const [initialProject, setInitialProject] = useState({
        title: '',
        description: '',
        isPublic: false,
        price: 0,
        file: null,
        image: null
    });

    const [projectTitle, setProjectTitle] = useState<string>('');
    const [isProjectPublic, setIsProjectPublic] = useState<boolean>(false);
    const [projectDescription, setProjectDescription] = useState<string>('');
    const [projectPrice, setProjectPrice] = useState<number>(0);
    const [projectCreationDate, setProjectCreationDate] = useState<string>();
    const [projectFile, setProjectFile] = useState<File | null>(null);
    const [projectFileInfos, setProjectFileInfos] = useState<any>(null);
    const [projectFileId, setProjectFileId] = useState<number>();
    const [projectImageId, setProjectImageId] = useState<number>();

    const pageTitle = isEditing ? "account.projects.edition" : "account.projects.creation";

    const initCreationPage = useCallback(async () => {
        setProjectTitle(t('account.projects.my_new_project'));
        setIsLoading(false);
    }, [t]);

    useEffect(() => {
        if (isEditing && projectId != null)
            handleGetProjectInfo(projectId);
        else if (!isEditing)
            initCreationPage();
    }, [isEditing, projectId, initCreationPage]);

    const isFormComplete = () => {
        const isTitleOk = projectTitle !== '' && projectTitle !== null;
        const isPriceOk = projectPrice === 0 || (projectPrice && projectPrice !== null && projectPrice > 0);
        return isTitleOk && isPriceOk;
    }

    const hasProjectChanged = () => {
        return (
            projectTitle !== initialProject.title ||
            projectDescription !== initialProject.description ||
            isProjectPublic !== initialProject.isPublic ||
            projectPrice !== initialProject.price ||
            projectFile !== initialProject.file
        );
    };

    const removeProjectFile = () => {
        setProjectFile(null);
        setProjectFileInfos(null);
    }

    const addProjectFile = (e: File | null) => {
        setProjectFile(e);
        setProjectFileInfos(e);
    }

    const handleGetProjectInfo = async (projectId: string) => {
        setIsLoading(true);

        const projectInfos = await getProjectById(projectId);

        setInitialProject({
            title: projectInfos.title,
            description: projectInfos.description || '',
            isPublic: projectInfos.isPublic,
            price: projectInfos.price,
            file: projectInfos.deriFile?.attributes,
            image: projectInfos.image?.attributes
        });

        setProjectTitle(projectInfos.title);
        setIsProjectPublic(projectInfos.isPublic);
        setProjectDescription(projectInfos.description || '');
        setProjectPrice(projectInfos.price);
        setProjectCreationDate(formatDate(projectInfos.createdAt));
        setProjectFileInfos(projectInfos.deriFile);
        setProjectFileId(projectInfos.deriFile?.id);
        setProjectImageId(projectInfos.image?.id);

        setIsLoading(false);
    }

    const extractImageFromZip = async (): Promise<File | null> => {
        try {
            if (!projectFile) return null;

            const buffer = await projectFile.arrayBuffer();
            const zipEntries = unzipSync(new Uint8Array(buffer));

            const imagePath = 'images/cover.png';

            const imageData: any = zipEntries[imagePath];

            if (!imageData) return null;

            return new File([imageData], 'cover.png', { type: 'image/png' });
        } catch (error) {
            console.error('Error extracting image from zip:', error);
            return null;
        }
    };


    const saveProject = async () => {
        setIsLoading(true);

         let project: project = {
            id: projectId,
            title: projectTitle,
            description: projectDescription,
            isPublic: isProjectPublic,
            price: projectPrice,
            fileId: projectFileId,
            imageId: projectImageId,
            deriFileInfos: projectFileInfos,
        }

        let projectImage = null;
        
        if (projectFile) {
            projectImage = await extractImageFromZip();

            project.deriFile = projectFile;

            if (!projectImage) {
                setNotificationMsg(t('account.projects.bad_file_format'));
                setIsNotificationOpen(true);
                setIsLoading(false);
                return;
            } else {
                project.image = projectImage;
            }
        }

        if (!projectFileInfos && !projectFile) {
            project.deriFile = null;
            project.image = null;
        }

        if (isEditing) {
            const res = await editProject(project, isTrustedUser);
            showNotification(res);
        } else {
            project.author = userId;
            const res = await createProject(project, isTrustedUser);

            showNotification(res);

            if (res)
                navigate('/account/projects');

            return;
        }

        setIsNotificationOpen(true);
        handleGetProjectInfo(projectId);
        setIsLoading(false);
    }

    const showNotification = (res: AxiosResponse<any, any> | undefined) => {
        if (res) {
            setNotificationMsg(t('account.projects.project_saved'));
        } else
            setNotificationMsg(t('general.error'));
    }

    return (
        <div className={styles.container}>
            { !isLoading &&
                <>
                    <div className={styles.containerTitle}>
                        <div className={styles.backBtn}>
                            <CustomIcon icon='arrow_back' label='retour' onClick={() => navigate(-1)} />
                        </div>
                        
                        <CustomTitle className={styles.title}>{ t(pageTitle) }</CustomTitle>
                    </div>

                    <CustomInputText id="projectTitle" label={t('account.projects.project_title')} type="text" textValue={projectTitle} setTextValue={setProjectTitle} error={projectTitle.length <= 0} helperText={projectTitle.length <= 0 ? t('general.required_field') : ''} required />
                    <CustomTextArea id="projectDescription" label={t('account.projects.description')} textValue={projectDescription} setTextValue={setProjectDescription} />
                    <CustomInputText id="projectPrice" label={t('account.projects.price')} type="number" textValue={projectPrice} setTextValue={setProjectPrice} error={!(projectPrice === 0 || (projectPrice && projectPrice !== null && projectPrice > 0))} helperText={!(projectPrice === 0 || (projectPrice && projectPrice !== null)) ? t('general.required_field') : ''} required />
                    <CustomFileInput accept=".deri" id="projectFile" label={t('account.projects.deri_file')} fileName={projectFileInfos?.name} file={projectFile} setValue={(e) => addProjectFile(e)} removeFile={() => removeProjectFile()} />
                    <CustomSwitch label={t('account.projects.is_public')} isChecked={isProjectPublic} onChange={() => setIsProjectPublic(!isProjectPublic)} />

                    { isEditing &&
                        <div className={styles.projectDate}>
                            <p>{ `${t('general.created_at')} ${projectCreationDate}` }</p>
                        </div>
                    }

                    <CustomButton className={styles.editBtn} onClick={() => saveProject()} disabled={!isFormComplete() || !hasProjectChanged()}>{t('general.save')}</CustomButton>
                </>
            }

            <CustomNotification
                message={notificationMsg}
                isOpen={isNotificationOpen}
                onClose={() => setIsNotificationOpen(false)}
            />
    
            <CustomLoader isLoading={isLoading} loaderType='skeleton' />
        </div>
    );
}

export default ProjectEdition;